import { useId } from 'react';
import { MotifLayer } from '../badges/badgeArt';
import type { Ribbon as RibbonData, RibbonArt } from '../../lib/types';

/**
 * A ribbon, composed rather than drawn — the same contract as `Badge.tsx`, and
 * for the same reason: the server sends `style`, `motif` and two colours, so a
 * ribbon seeded next year renders on a client built today.
 *
 * Where a badge is a *plate* (a solid silhouette with a metal frame that climbs
 * a tier ladder), a ribbon is a **rosette**: a medallion with fabric hanging
 * off it. That difference is deliberate and load-bearing. Badges belong to a
 * trainer and are ranked; ribbons belong to a Pokémon and are not. Drawing them
 * the same way would make a sash read as a second, competing cabinet — and the
 * first question anybody asked would be "which ribbon is best", which is the
 * one question ribbons are not for.
 *
 * There is no tier metal anywhere in this file. That is the point.
 */

export type RibbonStyle = 'rosette' | 'sash' | 'cockade' | 'plume' | 'star' | 'seal';

export function Ribbon({
  ribbon,
  size = 56,
  dimmed = false,
  title,
}: {
  ribbon: RibbonArt;
  size?: number;
  /** Unearned catalog entries: same shape, no colour. */
  dimmed?: boolean;
  title?: string;
}) {
  const id = useId();
  const base = dimmed ? '#8d9aa2' : ribbon.colorBase;
  const accent = dimmed ? '#c3cbd1' : ribbon.colorAccent;
  const style = (ribbon.style ?? 'rosette') as RibbonStyle;

  return (
    <svg
      viewBox="0 0 64 80"
      width={size}
      height={size * 1.25}
      className={`ribbon ribbon--${style} ${dimmed ? 'ribbon--dim' : ''}`}
      role="img"
      aria-label={title ?? ribbon.name}
      shapeRendering="geometricPrecision"
    >
      <title>{title ?? ribbon.name}</title>
      <defs>
        <linearGradient id={`${id}-fabric`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} />
          <stop offset="55%" stopColor={base} />
          <stop offset="100%" stopColor={base} stopOpacity={0.75} />
        </linearGradient>
        <linearGradient id={`${id}-disc`} x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor={accent} />
          <stop offset="60%" stopColor={base} />
          <stop offset="100%" stopColor={base} />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <circle cx="32" cy="26" r="17" />
        </clipPath>
      </defs>

      {/* Tails first, so the medallion always sits on top of its own fabric. */}
      <Tails style={style} id={id} />

      {/* The medallion. Every style shares it — what changes is what hangs
          beneath, which is what makes six styles distinguishable at 24px. */}
      <circle cx="32" cy="26" r="21" fill={base} stroke="var(--ink)" strokeWidth="2.5" />
      <circle cx="32" cy="26" r="17" fill={`url(#${id}-disc)`} />

      <g clipPath={`url(#${id}-clip)`}>
        <g transform="translate(20 14) scale(1.5)">
          <MotifLayer motif={ribbon.motif} fill="var(--ink)" highlight={accent} />
        </g>
      </g>

      <circle cx="32" cy="26" r="17" fill="none" stroke="var(--ink)" strokeWidth="1.5" opacity="0.5" />
      {/* A single highlight arc reads as gloss at any size, where a full radial
          gradient turns to mud below about 32px. */}
      <path d="M20 18a17 17 0 0 1 12-6" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.45" strokeLinecap="round" />
    </svg>
  );
}

/** What hangs below the medallion. The only thing that differs between styles. */
function Tails({ style, id }: { style: RibbonStyle; id: string }) {
  const fabric = `url(#${id}-fabric)`;
  const stroke = { stroke: 'var(--ink)', strokeWidth: 2 } as const;

  switch (style) {
    case 'sash':
      // One broad band, cut at the bottom. Reads as "worn", not "won".
      return (
        <path d="M22 40h20l-3 34-7-6-7 6z" fill={fabric} {...stroke} />
      );
    case 'plume':
      // Three tails: the busiest silhouette, saved for the rarest ribbons.
      return (
        <g fill={fabric} {...stroke}>
          <path d="M24 40h16l-2 30-6-5-6 5z" />
          <path d="M16 36l10 6-10 26-3-8-6 3z" />
          <path d="M48 36L38 42l10 26 3-8 6 3z" />
        </g>
      );
    case 'cockade':
      // Pleated fan behind the disc — a rosette turned up a notch.
      return (
        <g fill={fabric} {...stroke}>
          <path d="M32 4l8 8-8 6-8-6z" />
          <path d="M32 48l6 6-6 22-6-22z" />
          <path d="M10 20l10 4-4 10-8-6z" />
          <path d="M54 20L44 24l4 10 8-6z" />
        </g>
      );
    case 'star':
      // No fabric at all. A star ribbon is a pin, and the absence of tails is
      // what makes "Shining" and "Flawless" read as a different kind of thing.
      return null;
    case 'seal':
      // A wax seal with a torn strip: for the ribbons that are documents.
      return <path d="M18 42h28l-4 10H22z" fill={fabric} {...stroke} />;
    case 'rosette':
    default:
      return (
        <g fill={fabric} {...stroke}>
          <path d="M25 40h6l-4 30-5-6-5 5z" />
          <path d="M33 40h6l8 29-5-5-5 6z" />
        </g>
      );
  }
}

/**
 * The sash: every ribbon a Pokémon wears, laid out as a row.
 *
 * Wraps rather than scrolls. A Pokémon with forty ribbons should look like it
 * has forty ribbons — a scroll container would hide exactly the thing somebody
 * spent five years assembling.
 */
export function RibbonSash({
  ribbons,
  size = 44,
  onSelect,
  emptyLabel = 'No ribbons yet.',
}: {
  ribbons: RibbonData[];
  size?: number;
  onSelect?: (ribbon: RibbonData) => void;
  emptyLabel?: string;
}) {
  if (ribbons.length === 0) {
    return <p className="tiny muted" style={{ margin: 0 }}>{emptyLabel}</p>;
  }

  return (
    <div className="sash">
      {ribbons.map((ribbon) => {
        const art = (
          <Ribbon
            ribbon={ribbon}
            size={size}
            dimmed={ribbon.owned === false}
            title={ribbon.owned === false ? `${ribbon.name} — not yet earned` : ribbon.name}
          />
        );
        return onSelect ? (
          <button
            key={ribbon.ribbonKey}
            type="button"
            className="sash__slot"
            onClick={() => onSelect(ribbon)}
            aria-label={ribbon.name}
          >
            {art}
          </button>
        ) : (
          <span key={ribbon.ribbonKey} className="sash__slot">
            {art}
          </span>
        );
      })}
    </div>
  );
}
